import { ReactNode } from 'react';
import { useRole } from '@/contexts/role-context';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Shield, AlertTriangle } from 'lucide-react';

export type Permission =
  | 'manage_properties'
  | 'manage_users'
  | 'manage_templates'
  | 'manage_contracts'
  | 'perform_audits'
  | 'approve_audits'
  | 'view_reports'
  | 'manage_tasks';

export type RoleCapability =
  | 'canManageProperties'
  | 'canManageUsers'
  | 'canPerformAudits'
  | 'canApproveAudits'
  | 'canViewReports'
  | 'canManageTasks'
  | 'canManageTemplates'
  | 'canManageContracts';

// Define role capabilities based on new 4-role system
const roleCapabilities: Record<string, Record<RoleCapability, boolean>> = {
  super_admin: {
    canManageProperties: true,   // Full CRUD
    canManageUsers: true,         // Full CRUD
    canPerformAudits: true,       // Full CRUD
    canApproveAudits: true,
    canViewReports: true,
    canManageTasks: true,
    canManageTemplates: true,     // Full CRUD
    canManageContracts: true,
  },
  admin: {
    canManageProperties: false,   // View-only (no create/edit/delete)
    canManageUsers: true,         // Full CRUD (admin CAN manage users)
    canPerformAudits: true,       // Full assessment CRUD only
    canApproveAudits: true,
    canViewReports: true,
    canManageTasks: false,
    canManageTemplates: false,    // View-only (no create/edit/delete)
    canManageContracts: false,
  },
  manager: {
    canManageProperties: false,   // Only view own property
    canManageUsers: false,
    canPerformAudits: false,      // Cannot perform assessments
    canApproveAudits: false,
    canViewReports: true,         // Only missed standards
    canManageTasks: false,
    canManageTemplates: false,
    canManageContracts: false,
  },
  auditor: {
    canManageProperties: false,
    canManageUsers: false,
    canPerformAudits: true,       // Only perform assigned assessments
    canApproveAudits: false,
    canViewReports: false,
    canManageTasks: false,
    canManageTemplates: false,
    canManageContracts: false,
  },
};

interface RoleGuardProps {
  children: ReactNode;
  requiredRole?: string | string[];
  requiredPermission?: Permission | Permission[];
  requiredCapability?: RoleCapability | RoleCapability[];
  fallback?: ReactNode;
  allowedRoles?: string[];
  deniedMessage?: string;
}

export default function RoleGuard({
  children,
  requiredRole,
  requiredPermission,
  requiredCapability,
  fallback,
  allowedRoles,
  deniedMessage,
}: RoleGuardProps) {
  const { role, user } = useRole();
  const userCapabilities = roleCapabilities[role] || {};

  // If no user is available, deny access
  if (!user) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Authentication Required
          </CardTitle>
          <CardDescription>
            You must be logged in to access this resource.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Check role-based access
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(role)) {
      return (
        fallback || (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Access Denied
              </CardTitle>
              <CardDescription>
                {deniedMessage ||
                  `Only ${roles.join(', ')} can access this resource.`}
              </CardDescription>
            </CardHeader>
          </Card>
        )
      );
    }
  }

  // Check allowed roles (alternative approach)
  if (allowedRoles && !allowedRoles.includes(role)) {
    return (
      fallback || (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              {deniedMessage ||
                `Only ${allowedRoles.join(', ')} can access this resource.`}
            </CardDescription>
          </CardHeader>
        </Card>
      )
    );
  }

  // Check permission-based access (simplified for demo)
  if (requiredPermission) {
    const permissions = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];
    // For demo purposes, we'll map permissions to roles
    const permissionRoleMap: Record<Permission, string[]> = {
      manage_properties: ['admin', 'Manager'],
      manage_users: ['admin', 'Manager'],
      manage_templates: ['admin'],
      manage_contracts: ['admin'],
      perform_audits: ['admin', 'Auditor'],
      approve_audits: ['admin', 'Manager'],
      view_reports: ['admin', 'Manager', 'Department Head', 'Viewer'],
      manage_tasks: ['admin', 'Manager'],
    };

    const hasPermissions = permissions.every(permission =>
      permissionRoleMap[permission]?.includes(role)
    );

    if (!hasPermissions) {
      return (
        fallback || (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Insufficient Permissions
              </CardTitle>
              <CardDescription>
                {deniedMessage ||
                  `You don't have the required permissions: ${permissions.join(', ')}.`}
              </CardDescription>
            </CardHeader>
          </Card>
        )
      );
    }
  }

  // Check capability-based access
  if (requiredCapability) {
    const capabilities = Array.isArray(requiredCapability)
      ? requiredCapability
      : [requiredCapability];

    const hasCapabilities = capabilities.every(
      capability => userCapabilities[capability] === true
    );

    if (!hasCapabilities) {
      return (
        fallback || (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Access Denied
              </CardTitle>
              <CardDescription>
                {deniedMessage ||
                  `You don't have the required capabilities for this action.`}
              </CardDescription>
            </CardHeader>
          </Card>
        )
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
}

// Convenience components for common use cases
export function SuperAdminGuard({
  children,
  deniedMessage,
}: {
  children: ReactNode;
  deniedMessage?: string;
}) {
  return (
    <RoleGuard
      requiredRole="super_admin"
      deniedMessage={deniedMessage || 'Only super admin can access this feature.'}
    >
      {children}
    </RoleGuard>
  );
}

export function ManagerGuard({
  children,
  deniedMessage,
}: {
  children: ReactNode;
  deniedMessage?: string;
}) {
  return (
    <RoleGuard
      allowedRoles={['super_admin', 'admin']}
      deniedMessage={
        deniedMessage || 'Only super admin and admin can access this feature.'
      }
    >
      {children}
    </RoleGuard>
  );
}

export function TemplateManagementGuard({ children }: { children: ReactNode }) {
  return (
    <RoleGuard
      requiredCapability="canManageTemplates"
      deniedMessage="Only super admin can create, edit, or delete templates."
    >
      {children}
    </RoleGuard>
  );
}

export function UserManagementGuard({ children }: { children: ReactNode }) {
  return (
    <RoleGuard
      requiredCapability="canManageUsers"
      deniedMessage="Only super admin can create, edit, or delete users."
    >
      {children}
    </RoleGuard>
  );
}

export function PropertyManagementGuard({ children }: { children: ReactNode }) {
  return (
    <RoleGuard
      requiredCapability="canManageProperties"
      deniedMessage="Only super admin can create, edit, or delete properties."
    >
      {children}
    </RoleGuard>
  );
}
